import { normalizePdpImages, typedBoolean } from "@jog/shared"
import { TweakwiseItem } from "@jog/types"
import { kebabCase } from "lodash-es"
import { useRouter } from "next/router"
import { type CSSProperties, useMemo, useState } from "react"

import { cn } from "../../ui"
import { ItemImage } from "../ListerTileImage/ListerTileImage"

export interface ListerTileProps {
    data: TweakwiseItem
    group: TweakwiseItem[]
    className?: string
    onClick: (item: TweakwiseItem) => void
}

export const getColor = (item: TweakwiseItem) =>
    item?.attributes?.find((val) => val.name === "color" && val.values)?.values?.[0] || "default"
const colMap: Record<string, string> = {
    "1": "col-span-1",
    "2": "col-span-2",
    "3": "md:col-span-3 col-span-2",
    "4": "md:col-span-4 col-span-2",
}
interface AspectRatioProperties extends CSSProperties {
    ["--desktop-aspect-ratio"]: string
}
const rowMap: Record<string, string> = {
    "1": "row-span-1",
    "2": "row-span-2",
    "3": "row-span-3",
    "4": "row-span-4",
}
export const getTweakwiseGrid = (data: TweakwiseItem): { grid: string; aspectRatio: AspectRatioProperties } => {
    if (!data?.visualproperties) {
        return {
            grid: "col-span-1 row-span-1",
            aspectRatio: { ["--desktop-aspect-ratio"]: "auto" },
        }
    }
    const { colspan, rowspan } = data.visualproperties
    const col = colMap[colspan] || ""
    const row = rowMap[rowspan] || ""
    return {
        grid: cn(col, row),
        aspectRatio: { ["--desktop-aspect-ratio"]: colspan === 4 ? "auto" : `${colspan}/${rowspan}` },
    }
}

export const useTile = (data: TweakwiseItem, _group: TweakwiseItem[]) => {
    const [activeProduct, setActiveProduct] = useState<TweakwiseItem>(data)
    const product = useProduct(data)
    const [fold, setFold] = useState(true)
    const listerPageTileImage = useMemo(() => {
        const thumbnailImageData = activeProduct.attributes.find((attribute) => attribute.name === "thumbnailimage")
        return thumbnailImageData?.values?.[0] || activeProduct.image
    }, [activeProduct])
    const hasGroup = useMemo(() => _group && _group.length > 1, [_group])
    const group = useMemo(() => {
        if (!hasGroup) return []
        const map = _group.reduce<Record<string, TweakwiseItem>>((pre, cur) => ({ ...pre, [cur.itemno]: cur }), {})
        const colorMap: Record<string, string> = { [getColor(data)]: data.itemno }
        _group.forEach((item) => {
            const color = getColor(item)
            if (!colorMap[color]) {
                colorMap[color] = item.itemno
            }
        })
        return Array.from(new Set([...Object.values(colorMap), ..._group.map((i) => i.itemno)])).map((i) => map[i])
    }, [_group, data, hasGroup])
    const promotion = data.attributes.find((val) => val.name === "discountName" && val.values)?.values?.at(0) || ""

    return {
        activeProduct,
        setActiveProduct,
        listerPageTileImage,
        product,
        fold,
        toggle: () => setFold((v) => !v),
        group,
        hasGroup,
        promotion,
    }
}
export const parseExtension = (json: string): { articleName: string; colorCode: string; lowestPrice: number } => {
    try {
        return JSON.parse(json) as { articleName: string; colorCode: string; lowestPrice: number }
    } catch {
        return { articleName: "", colorCode: "", lowestPrice: 0 }
    }
}
export const getProductPrice = (product: TweakwiseItem) => {
    const firstPrice =
        product.attributes?.find((val) => val.name?.toLowerCase() === "firstprice" && val.values)?.values?.[0] || "0"
    const hasDiscount = product.attributes?.find((val) => val.name === "discountName" && val.values)
    const { lowestPrice } = parseExtension(
        product.attributes?.find((val) => val.name?.toLowerCase() === "extension" && val.values)?.values?.[0] || "{}"
    )
    return { firstPrice, hasDiscount, lowestPrice }
}
export const getProductUrl = (product: TweakwiseItem) => {
    const categories = product.attributes?.find((val) => val.name === "categoryAsUrl" && val.values)?.values?.[0]
    const name = product.title
    const slug = product.attributes?.find((val) => val.name === "slug" && val.values)?.values?.[0]
    return `/${categories}/${kebabCase(name)}/${kebabCase(slug)}`
}
export const useProduct = (product: TweakwiseItem) =>
    useMemo(() => {
        const name = product.title
        const { firstPrice, hasDiscount, lowestPrice } = getProductPrice(product)
        const linkItem = getProductUrl(product)
        const isDiscount = (hasDiscount || firstPrice) && Number(firstPrice) > Number(product.price)
        return {
            lowestPrice,
            firstPrice,
            name,
            linkItem,
            isDiscount,
            image: product.image,
            price: product.price,
        }
    }, [product])

export const useGallery = (data: TweakwiseItem, desktopSortingByName?: string[], mobileSortingByName?: string[]) => {
    const router = useRouter()
    const images = useMemo(
        () =>
            (data.attributes.find((attribute) => attribute.name === "gallery")?.values || [])
                ?.filter(typedBoolean)
                .map((item) => JSON.parse(item) as ItemImage),
        [data]
    )
    const mobile = useMemo(
        () => normalizePdpImages(images, mobileSortingByName || [], []),
        [images, mobileSortingByName]
    )
    const desktop = useMemo(() => {
        // When there is no flatshot image available then the hover/swipe functionality is not needed
        // When there is only a flatshot image available then the hover/swipe functionality is not needed
        const _images = normalizePdpImages(images, desktopSortingByName || [], [])
        const onmodelFront = _images.find((i) => i.label.startsWith("onmodel-front"))
        const flatshotsImage = _images.find((i) => i.label.startsWith("flatshots-front"))
        const onmodelBack = _images.find((i) => i.label.startsWith("onmodel-back"))
        const main = onmodelFront || flatshotsImage
        if (router.query.slug?.includes("jeans")) {
            // jeans shows `onmodel-back`
            return [main, onmodelBack || flatshotsImage].filter(typedBoolean)
        }
        return [main, flatshotsImage].filter(typedBoolean)
    }, [desktopSortingByName, images, router.query.slug])
    return { mobile, desktop }
}
